/**
 * React Hook for Project Index Cache Management
 * 
 * Provides reactive access to the project navigation cache with:
 * - Automatic user authentication integration
 * - Reactive updates via custom events
 * - Cache initialization and cleanup
 * - Error handling and fallbacks
 */

import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@/hooks/auth/use-user';
import { 
  ProjectIndexData, 
  getProjectIndex, 
  saveProjectIndex, 
  clearProjectIndex,
  updateProjectInCache,
  removeProjectFromCache,
  updatePageInProjectCache,
  removePageFromProjectCache,
  getAIProjectContext,
  findProjectByPageTitle,
  getCacheStats
} from '@/utils/project-navigation-cache';

/**
 * Hook return interface
 */
interface UseProjectIndexReturn {
  // Cache data
  projectIndex: ProjectIndexData | null;
  isLoading: boolean;
  error: string | null;
  
  // Cache operations
  updateProject: (projectData: {
    id: string;
    title: string;
    type?: string;
    overview?: string;
    chatroomId?: string;
    goal?: { id: string; title: string; };
  }) => void;
  removeProject: (projectId: string) => void;
  
  // Page operations
  updatePage: (projectId: string, pageData: {
    id: string;
    title: string;
    type: string;
    hasContent?: boolean;
  }) => void;
  removePage: (projectId: string, pageId: string) => void;
  
  // AI integration
  getAIContext: () => string;
  findPageProject: (pageTitle: string) => {
    projectId: string;
    pageId: string;
    chatroomId?: string;
  } | null;
  
  // Cache management
  refreshCache: () => void;
  clearCache: () => void;
  getCacheStatistics: () => any;
}

/**
 * Main project index cache management hook
 */
export const useProjectIndex = (): UseProjectIndexReturn => {
  const { user } = useUser();
  const [projectIndex, setProjectIndex] = useState<ProjectIndexData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Force re-render when cache updates
  const [updateKey, setUpdateKey] = useState(0);
  const triggerUpdate = useCallback(() => {
    setUpdateKey(prev => prev + 1);
  }, []);
  
  /**
   * Load cache from localStorage
   */
  const loadCache = useCallback(() => {
    try {
      setIsLoading(true);
      setError(null);
      
      const cached = getProjectIndex();
      
      // Validate user matches
      if (cached && user?.id && cached.userId !== user.id) {
        console.log('[ProjectIndex] User mismatch, clearing cache');
        clearProjectIndex();
        setProjectIndex(null);
      } else {
        setProjectIndex(cached);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('[ProjectIndex] Error loading cache:', err);
      setError(err instanceof Error ? err.message : 'Failed to load cache');
      setIsLoading(false);
    }
  }, [user?.id]);
  
  /**
   * Initialize cache when user changes
   */
  useEffect(() => {
    if (user?.id) {
      loadCache();
    } else {
      // Clear cache when user logs out
      setProjectIndex(null);
      setIsLoading(false);
      setError(null);
    }
  }, [user?.id, loadCache]);
  
  /**
   * Set up reactive listeners for cache updates
   */
  useEffect(() => {
    const handleCacheUpdate = (event: Event) => {
      console.log('[ProjectIndex] Cache updated, refreshing');
      loadCache();
    };
    
    const handleCacheCleared = () => {
      console.log('[ProjectIndex] Cache cleared');
      setProjectIndex(null);
      setError(null);
    };
    
    // Listen for cache update events
    window.addEventListener('project-index-updated', handleCacheUpdate);
    window.addEventListener('project-index-cleared', handleCacheCleared);
    
    return () => {
      window.removeEventListener('project-index-updated', handleCacheUpdate);
      window.removeEventListener('project-index-cleared', handleCacheCleared);
    };
  }, [loadCache]);
  
  /**
   * Update project in cache
   */
  const updateProject = useCallback((projectData: {
    id: string;
    title: string;
    type?: string;
    overview?: string;
    chatroomId?: string;
    goal?: { id: string; title: string; };
  }) => {
    if (!user?.id) {
      console.warn('[ProjectIndex] Cannot update project: no user');
      return;
    }
    
    updateProjectInCache(projectData, user.id);
  }, [user?.id]);
  
  /**
   * Remove project from cache
   */
  const removeProject = useCallback((projectId: string) => {
    if (!user?.id) {
      console.warn('[ProjectIndex] Cannot remove project: no user');
      return;
    }
    
    removeProjectFromCache(projectId, user.id);
  }, [user?.id]);
  
  /**
   * Update page in project cache
   */
  const updatePage = useCallback((projectId: string, pageData: {
    id: string;
    title: string;
    type: string;
    hasContent?: boolean;
  }) => {
    if (!user?.id) {
      console.warn('[ProjectIndex] Cannot update page: no user');
      return;
    }
    
    updatePageInProjectCache(projectId, pageData, user.id);
  }, [user?.id]);
  
  /**
   * Remove page from project cache
   */
  const removePage = useCallback((projectId: string, pageId: string) => {
    if (!user?.id) {
      console.warn('[ProjectIndex] Cannot remove page: no user');
      return;
    }
    
    removePageFromProjectCache(projectId, pageId, user.id);
  }, [user?.id]);
  
  /**
   * Get AI context string
   */
  const getAIContext = useCallback((): string => {
    return getAIProjectContext();
  }, []);
  
  /**
   * Find project by page title
   */
  const findPageProject = useCallback((pageTitle: string) => {
    return findProjectByPageTitle(pageTitle);
  }, []);
  
  /**
   * Refresh cache manually
   */
  const refreshCache = useCallback(() => {
    loadCache();
  }, [loadCache]);
  
  /**
   * Clear cache manually
   */
  const clearCache = useCallback(() => {
    clearProjectIndex();
  }, []);
  
  /**
   * Get cache statistics
   */
  const getCacheStatistics = useCallback(() => {
    return getCacheStats();
  }, []);
  
  return {
    // Cache data
    projectIndex,
    isLoading,
    error,
    
    // Cache operations
    updateProject,
    removeProject,
    
    // Page operations
    updatePage,
    removePage,
    
    // AI integration
    getAIContext,
    findPageProject,
    
    // Cache management
    refreshCache,
    clearCache,
    getCacheStatistics
  };
};

/**
 * Lightweight hook for just AI context (no reactive updates)
 * Use this when you only need the AI context string
 */
export const useProjectAIContext = (): {
  getContext: () => string;
  hasProjects: boolean;
} => {
  const [hasProjects, setHasProjects] = useState(false);
  
  // Check project availability on mount and when cache updates
  useEffect(() => {
    const checkProjects = () => {
      const cached = getProjectIndex();
      setHasProjects(cached?.creationContext?.hasProjects || false);
    };
    
    checkProjects();
    
    // Listen for cache updates
    const handleCacheUpdate = () => checkProjects();
    window.addEventListener('project-index-updated', handleCacheUpdate);
    window.addEventListener('project-index-cleared', () => setHasProjects(false));
    
    return () => {
      window.removeEventListener('project-index-updated', handleCacheUpdate);
      window.removeEventListener('project-index-cleared', () => setHasProjects(false));
    };
  }, []);
  
  const getContext = useCallback(() => {
    return getAIProjectContext();
  }, []);
  
  return {
    getContext,
    hasProjects
  };
};

/**
 * Hook for page-specific project lookup
 * Useful for "OPEN AI TEXT MAGIC" functionality
 */
export const usePageProjectLookup = () => {
  const findProject = useCallback((pageTitle: string) => {
    return findProjectByPageTitle(pageTitle);
  }, []);
  
  return { findProject };
};

/**
 * Development/debug hook for cache monitoring
 * Only use in development mode
 */
export const useProjectCacheDebug = () => {
  const [stats, setStats] = useState<any>(null);
  
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    
    const updateStats = () => {
      setStats(getCacheStats());
    };
    
    updateStats();
    
    // Update stats when cache changes
    window.addEventListener('project-index-updated', updateStats);
    window.addEventListener('project-index-cleared', updateStats);
    
    return () => {
      window.removeEventListener('project-index-updated', updateStats);
      window.removeEventListener('project-index-cleared', updateStats);
    };
  }, []);
  
  return {
    stats,
    refreshStats: () => setStats(getCacheStats())
  };
}; 